import { PageHero, SectionTitle } from "@/components/Shared";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import PressSection from "@/components/PressSection";
import MSG2022Section from "@/components/MSG2022Section";
import { GraduationCap, Target, Flag, HeartHandshake } from "lucide-react";
import { IMAGES, LOGOS } from "@/data/site";

const pillars = [
  { icon: GraduationCap, t: "Driver Education", d: "Structured coaching covering race lines, braking, overtaking and race craft for young drivers." },
  { icon: Target, t: "Talent Pathway", d: "A clear route from first laps to competitive club racing under the FIA Karting framework." },
  { icon: Flag, t: "Club Racing", d: "Regular club meets and championships give students real, timed competitive experience." },
  { icon: HeartHandshake, t: "Community & Safety", d: "A safe, inclusive environment focused on discipline, respect and motorsport values." },
];

export default function EduKarting() {
  return (
    <div data-testid="edukarting-page">
      <PageHero
        eyebrow="YOUTH DEVELOPMENT"
        title="EduKarting Bahamas"
        subtitle="Our grassroots partner and FIA Karting club, developing the next generation of Bahamian motorsport talent."
        image="/edukart/ek-c.jpg"
        testid="edukarting-hero"
      />

      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 md:grid-cols-2 md:px-10">
          <Reveal>
            <div className="rounded-xl border border-white/10 bg-obsidian p-10">
              <img src={LOGOS.edukarting} alt="EduKarting Bahamas" className="h-28 w-auto" />
              <p className="mt-8 leading-relaxed text-white/60">
                EduKarting Bahamas is a registered <span className="text-white">non-profit organisation (NPO)</span>
                {" "}and FIA Karting club dedicated to youth driver development and motorsport education. Karting
                Bahamas is proud to be its <span className="text-kbcyan">new home</span> — and every dollar of
                proceeds from the track goes directly to funding the EduKarting programme.
              </p>
              <p className="mt-4 leading-relaxed text-white/60">
                From their very first laps in a junior kart to competitive club racing, students
                learn far more than speed — they learn focus, discipline, teamwork and respect.
              </p>
            </div>
          </Reveal>
          <div>
            <SectionTitle eyebrow="THE PROGRAM" title="From Grassroots To Grid" />
            <p className="mt-6 max-w-lg leading-relaxed text-white/60">
              As <span className="text-white">the only official FIA Motor Sports Club in The Bahamas</span>,
              Karting Bahamas can sanction all forms of motorsport — including karting — and is the only club
              able to issue both <span className="text-white">national and international FIA racing licenses</span>.
              That gives EduKarting students a genuine, recognised pathway into world motorsport.
            </p>
            <p className="mt-4 max-w-lg leading-relaxed text-white/60">
              Our credentials also make us the <span className="text-white">only club that can officially
              represent The Bahamas on the world stage</span> — including at the{" "}
              <a href="https://www.fiamotorsportgames.com/" target="_blank" rel="noreferrer" className="text-kbcyan underline decoration-kbcyan/40 underline-offset-4 hover:decoration-kbcyan">
                FIA Motorsport Games
              </a>, the "Motorsport Olympics" where nations compete for their flag. We proudly took a team to the{" "}
              <a href="https://www.tribune242.com/news/2023/mar/21/karting-taking-242-world/" target="_blank" rel="noreferrer" className="text-kbcyan underline decoration-kbcyan/40 underline-offset-4 hover:decoration-kbcyan">
                2022 FIA Motorsport Games
              </a>.
            </p>
            <p className="mt-4 max-w-lg leading-relaxed text-white/60">
              When you race with us, you're not just having fun — you're directly funding the next generation
              of Bahamian racing talent.
            </p>
            <div className="mt-8 flex items-center gap-6">
              <img src={LOGOS.bac} alt="Bahamas Automobile Club" className="h-10 w-auto opacity-80" />
              <span className="text-sm text-white/40">Sanctioned under the Bahamas Motor Sports Federation &amp; Bahamas Automobile Club</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="WHAT WE BUILD" title="Four Pillars Of Development" className="mb-16" />
          <StaggerGroup className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p) => (
              <StaggerItem key={p.t}>
                <div className="group h-full rounded-xl border border-white/10 bg-[#0A0A0A] p-8 transition-all hover:-translate-y-2 hover:border-kbyellow/50">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-kbyellow/10 text-kbyellow transition-colors group-hover:bg-kbyellow group-hover:text-black">
                    <p.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl tracking-wide text-white">{p.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{p.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <MSG2022Section />

      <PressSection bg="bg-[#050505]" />

      <section className="relative overflow-hidden py-28 md:py-36">
        <img src={IMAGES.eduKids} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-[#050505]" />
        <div className="relative mx-auto max-w-[1400px] px-5 text-center md:px-10">
          <h2 className="mx-auto max-w-3xl font-display text-5xl leading-[0.9] tracking-tight text-white md:text-7xl">
            Think Your Young Driver Has What It Takes?
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-white/60">
            Get in touch to learn about junior sessions, coaching and how to join the EduKarting pathway.
          </p>
          <a
            href="/contact"
            data-testid="edukarting-contact-cta"
            className="mt-10 inline-block rounded-xl bg-kbcyan px-8 py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.03]"
          >
            Enquire About EduKarting
          </a>
        </div>
      </section>
    </div>
  );
}
